import { useMemo } from 'react';
import { useAuthStore } from '../auth/store';
import { navActionsForForm, type NavActions } from '../layouts/navPermissions';

/**
 * Effective per-form ACTION permissions (Read / Write / Create / Delete) for the
 * signed-in user, resolved from their assigned Module Access Control (Role-Based
 * MAC) levels.  Use it to gate a form's mutating buttons:
 *
 *   const perm = useNavActions('repval');
 *   {perm.delete && <button onClick={onDelete}>Delete</button>}   // hide, not disable
 *
 * `formId` is the nav id from `navData.ts` / `NAV_VIEW_MAPPING` (e.g. 'repval',
 * 'repdel', 'custreport').  Falls open (all true) for admins, before permissions
 * load, or for a form with no MAC action rows — so it only ever TIGHTENS access
 * where the lab has configured a MAC level, never removes an existing button.
 */
export function useNavActions(formId: string): NavActions {
  const perms = useAuthStore(s => s.permissions);
  const userName = useAuthStore(s => s.user?.userName ?? s.user?.name ?? '');
  // The `service` operator and the `Administrator` login are never restricted by MAC (owner 2026-10-28: both
  // always get every form's full toolbar). Other admins already fall open inside navActionsForForm via isAdmin.
  const login = userName.trim().toLowerCase();
  const isFullRights = login === 'service' || login === 'administrator';
  return useMemo(
    () => navActionsForForm(formId, perms?.navActions, (perms?.isAdmin ?? false) || isFullRights),
    [formId, perms, isFullRights],
  );
}

export type { NavActions };
